'use client';
import { MSProductWithFullDetailWithVaraintPreset } from '@/util/query/product-query';
import { toPriceString } from '@/util/toPriceString';
import { CUSTOMER_LEVEL } from '@prisma/client';
import { Loader2 } from 'lucide-react';
import { useSession } from 'next-auth/react';
import dynamic from 'next/dynamic';
import { useRouter } from 'next/navigation';
import { memo, useEffect, useState } from 'react';
import toast from 'react-hot-toast';
// import { CustomTooltip } from "../ui/Tooltip/custom-tooltip";
import * as actions from '@/actions';
import { useTranslation } from '@/app/i18n/client';
import BagCartButton from '@/assets/icons/bag-cart.svg?react';
import CollectionListItemClient from '@/components/collections/collection-list-favorite-client';
import ProductTagRenderer from '@/components/product-tag-renderer';
import SmartImage from '@/components/SmartImage';
import { useCart } from '@/lib/providers/cart-provider';
import ColorStyleContext from '@/lib/providers/color-style-privider';
import {
    borderRadiusShorthand,
    useLayoutStyle,
} from '@/lib/providers/layout-style-provider';
import { applyDiscountPromotion } from '@/util/applyPromotion';
import {
    getBestPriceByType,
    getPromotionDiscountedPrice,
    StepPriceType,
} from '@/util/promotion';
import { AddItemToCartRequestParams } from '@/util/services/cart/api';
import { formatCurrencyFormat } from '@/util/util';
import { useContext } from 'react';
import './product-card.css';
const CartNotification = dynamic(
    () => import('@/components/cart/cart-notification'),
    { ssr: false },
);

interface CollectionListItemProps {
    product: MSProductWithFullDetailWithVaraintPreset;
    href: string;
    // t: TFunction<"translation", undefined>;
    level: CUSTOMER_LEVEL;
    refreshfav?: boolean;
    variantId?: string;
    priorityImage?: boolean;
    shopCurrency: string;
    favoriteEnable: boolean;
}

// Helper component to render currency smaller than the numeric price.
// shopCurrency format example: "฿ {price}" or "{price} ฿" (always contains "{price}").
// We split around {price} and render currency parts with smaller font-size.
interface CurrencyPriceProps {
    priceString: string; // already formatted numeric string (e.g., 230 or 1,250.00)
    shopCurrency: string; // pattern with {price}
    wrapperClassName?: string; // classes applied to outer wrapper
    priceClassName?: string; // classes for the numeric part
    currencyClassName?: string; // classes for currency part (defaults to smaller text)
    id?: string;
}

const CurrencyPrice = ({
    priceString,
    shopCurrency,
    wrapperClassName = '',
    priceClassName = '',
    currencyClassName = 'text-sm',
    id,
}: CurrencyPriceProps) => {
    const parts = shopCurrency.split('{price}');
    const prefix = parts[0] ? parts[0].trim() : '';
    const suffix = parts[1] ? parts[1].trim() : '';
    return (
        <span
            id={id}
            className={`inline-flex items-baseline gap-1 ${wrapperClassName}`}
        >
            {prefix && <span className={currencyClassName}>{prefix}</span>}
            <span className={priceClassName}>{priceString}</span>
            {suffix && <span className={currencyClassName}>{suffix}</span>}
        </span>
    );
};

const ProductCardButtonBesidePriceInner = ({
    product,
    href,
    // t,
    level,
    refreshfav,
    variantId,
    priorityImage,
    shopCurrency,
    favoriteEnable,
}: CollectionListItemProps) => {
    const { t, loc } = useTranslation();
    // Equalize heights of the two dynamic blocks across all product cards so layout rows align.
    useEffect(() => {
        const syncHeights = () => {
            const infoBlocks = Array.from(
                document.querySelectorAll<HTMLDivElement>(
                    '#product-card-button-beside-price-info .product-item-next-step-info',
                ),
            );
            const normalBlocks = Array.from(
                document.querySelectorAll<HTMLDivElement>(
                    '#product-card-button-beside-price-info .product-item-next-step-info-normal',
                ),
            );
            // Reset heights to natural first
            infoBlocks.forEach((el) => (el.style.height = 'auto'));
            normalBlocks.forEach((el) => (el.style.height = 'auto'));
            const maxInfo = infoBlocks.reduce(
                (m, el) => Math.max(m, el.offsetHeight),
                0,
            );
            const maxNormal = normalBlocks.reduce(
                (m, el) => Math.max(m, el.offsetHeight),
                0,
            );
            infoBlocks.forEach((el) => (el.style.height = maxInfo + 'px'));
            normalBlocks.forEach((el) => (el.style.height = maxNormal + 'px'));
        };
        // Run after mount & on images load slight delay
        const timeout = setTimeout(syncHeights, 50);
        window.addEventListener('resize', syncHeights);
        return () => {
            clearTimeout(timeout);
            window.removeEventListener('resize', syncHeights);
        };
    }, []);
    const inStockVariants = product.productVariant.filter(
        (v) => v.orderType !== 'DISABLED' && v.inventory - v.inventory_used > 0,
    );
    const defaultVariant =
        inStockVariants.length > 0
            ? inStockVariants.reduce((a, b) =>
                  a.priceFull < b.priceFull ? a : b,
              )
            : product.productVariant[0] ?? null;
    let variant = product.productVariant.length > 0 ? defaultVariant : null;
    if (variantId) {
        const variantsFilter = product.productVariant.filter(
            (x) => x.id === variantId,
        );
        if (variantsFilter.length > 0) {
            variant = variantsFilter[0];
        }
    }
    const title = product.attributes?.displayName ?? '';
    let cv: number | null;
    let qv: number | null;
    if (level === 'BP_VVIP') {
        cv = variant ? variant.CV_VVIP : null;
        qv = variant ? variant.QV_VVIP : null;
    } else if (level === 'BP_VIP') {
        cv = variant ? variant.CV_VIP : null;
        qv = variant ? variant.QV_VIP : null;
    } else {
        cv = variant ? variant.CV : null;
        qv = variant ? variant.QV : null;
    }

    const promotionDiscountedPrice = variant
        ? getPromotionDiscountedPrice(variant)
        : ({} as any);
    const rawStepTiers = promotionDiscountedPrice.STEP_PRICE?.value; // StepPriceValue[]
    // For collection list we assume quantity in cart = 0 (preview). So currentStep will be undefined and nextStep first tier.
    const stepSelection: StepPriceType | null = rawStepTiers
        ? (getBestPriceByType(rawStepTiers, 'STEP_PRICE', 0) as StepPriceType)
        : null;
    const nextStep = stepSelection?.nextStep;

    // Apply Discount Promotion price
    variant = applyDiscountPromotion(promotionDiscountedPrice, variant);

    // Effective special price for display. Step price is a special price: apply it
    // only when it beats the existing special/normal price. Kept in locals (we do NOT
    // mutate variant) so re-renders never compound the discount.
    let displayCompareFull = variant?.comparePriceFull ?? null;
    let displayCompareTrial = variant?.comparePriceTrial ?? null;
    if (variant && nextStep) {
        const stepFull = nextStep.price['GUEST']?.price;
        const stepTrial = nextStep.price['BP']?.price;
        if (stepFull != null)
            displayCompareFull = Math.min(
                stepFull,
                displayCompareFull ?? variant.priceFull,
            );
        if (stepTrial != null)
            displayCompareTrial = Math.min(
                stepTrial,
                displayCompareTrial ?? variant.priceTrial,
            );
    }

    const trialNormalPrice = variant ? variant.priceTrial : null;
    const normalPrice = variant ? variant.priceFull : null;
    // Base the step discount is measured from: special price when set & lower
    // (same base as getPromotionDiscountedPrice), else the list price.
    const stepNormalPrice = !variant
        ? 0
        : level === 'GUEST'
          ? variant.comparePriceFull != null &&
            variant.comparePriceFull < variant.priceFull
              ? variant.comparePriceFull
              : variant.priceFull
          : variant.comparePriceTrial != null &&
              variant.comparePriceTrial < variant.priceTrial
            ? variant.comparePriceTrial
            : variant.priceTrial;
    const isTrialHaveSpecialPrice =
        variant && displayCompareTrial != null
            ? displayCompareTrial < variant.priceTrial
            : false;
    const isFullHaveSpecialPrice =
        variant && displayCompareFull != null
            ? displayCompareFull < variant.priceFull
            : false;
    const trialPrice = isTrialHaveSpecialPrice
        ? displayCompareTrial
        : variant
          ? variant.priceTrial
          : null;
    const price = isFullHaveSpecialPrice
        ? displayCompareFull
        : variant
          ? variant.priceFull
          : null;

    var imageUrl = product.featuredImageUrl
        ? product.featuredImageUrl
        : (variant ? variant.featuredImageUrl : '') || '';

    const imageIndex = product.imagesUrl.findIndex(
        (image) => image === imageUrl,
    );
    const imageAlt = product.imagesAlt[imageIndex] || `product-${product.id}`;

    const colorStyle = useContext(ColorStyleContext);

    const session = useSession();
    const { addItem, getQuantityForItem } = useCart({ loc: 'th' });
    const router = useRouter();
    const layoutStyle = useLayoutStyle();

    const [requireQuantity, setRequireQuantity] = useState<number>(1);
    const [isAddingItem, setAddingItem] = useState<boolean>(false);

    const orderType = variant?.orderType ?? 'DISABLED';
    const isOutOfStock = variant
        ? variant.inventory - variant.inventory_used <= 0
        : false;
    const quantityInCart = variant ? getQuantityForItem(variant.id) : 0;
    const stock = variant ? variant.inventory - variant.inventory_used : 0;
    const isAddAllStockInCart = quantityInCart >= stock && stock > 0;

    let text: { [key: string]: string } = {};
    text['weight_2'] = t('product.weight_2');
    text['share_btn_share'] = t('share.btn_share');
    text['share_recommend_business'] = t('share.recommend_business');
    text['share_open_opportunities'] = t('share.open_opportunities');
    text['product_promotion_special'] = t('product.promotion_special');
    text['product_out_of_stock'] = t('product.out_of_stock');
    text['product_all_stock_incart'] = t('product.all_stock_incart');
    text['product_btn_register'] = t('product.btn_register');
    text['product_delivery_fee'] = t('product.delivery_fee');
    text['product_delivery_fee_condition'] = t(
        'product.delivery_fee_condition',
    );
    text['product_sold_out'] = t('product.sold_out');
    text['product_coming_soon'] = t('product.coming_soon');
    text['product_vvip_subscription_price'] = t(
        'product.vvip_subscription_price',
    );
    text['product_vip_subscription_price'] = t(
        'product.vip_subscription_price',
    );
    text['product_trial_price'] = t('product.trial_price');
    text['product_price'] = t('product.price');
    text['product_cv'] = t('product.cv');
    text['product_qv'] = t('product.qv');
    text['product_trial_tip'] = t('product.trial_tip');
    text['quantity'] = t('quantity');
    text['product_btn_add_to_quote'] = t('product.add_to_quote');
    text['product_btn_buy_trial'] = t('product.btn_buy_trial');
    text['product_btn_buy_1'] = t('product.btn_buy_1');
    text['product_btn_compare'] = t('product.product_btn_compare');
    text['cart_add_to_cart'] = t('cart.add_to_cart');
    text['cart_add_to_quote'] = t('cart.add_to_quote');
    text['cart_btn_view_cart2'] = t('cart.btn_view_cart2');
    text['cart_btn_view_quote_cart2'] = t('cart.btn_view_quote_cart2');
    text['cart_btn_checkout'] = t('cart.btn_checkout');
    text['cart_btn_create_quote'] = t('cart.btn_create_quote');
    text['product_specification_title'] = t('product.product_information');
    text['all_products_available'] = t('product.all_products_available');
    text['view_product_detail'] = t('product.go_to_product_deatil');
    text['product_image'] = t('tab_navigator.product_image');
    text['product_name'] = t('tab_navigator.product_name');
    text['compare'] = t('tab_navigator.compare');
    text['product_link'] = t('tab_navigator.product_link');
    text['go_to_form'] = t('product.go_to_form');
    text['load_more'] = t('tab_navigator.load_more');
    text['select'] = t('tab_navigator.select');
    text['select_all'] = t('tab_navigator.select_all');

    const handleOnAddCart = async () => {
        const user = await actions.getUser();
        if (!variant) {
            return;
        }

        const productToAdd = {
            idProduct: product.id,
            idVariant: variant.id,
            quantity: requireQuantity,
        };
        if (!user && session.status === 'authenticated') {
            await actions.createGuest(false);
        }
        if (session.status === 'authenticated') {
            await addItemToCart(productToAdd);
        } else if (session.status === 'unauthenticated') {
            await actions.createGuest(false);
            await addItemToCart(productToAdd);
            setTimeout(() => window.location.reload(), 1000);
        }
    };

    const addItemToCart = async (productToAdd: AddItemToCartRequestParams) => {
        if (!variant) {
            return;
        }
        setAddingItem(true);

        try {
            await addItem(productToAdd);

            setAddingItem(false);
            toast.custom(
                (t) => (
                    <CartNotification
                        visible={t.visible}
                        onClose={() => toast.remove(t.id)}
                        product={product}
                        variant={variant}
                        type={
                            level === 'BP_VVIP'
                                ? 'vvip'
                                : level === 'BP_VIP'
                                  ? 'vip'
                                  : level === 'BP'
                                    ? 'trial'
                                    : level === 'NORMAL'
                                      ? 'trial'
                                      : 'normal'
                        }
                        loc={loc}
                        text={text}
                        shopCurrency={shopCurrency}
                        color={colorStyle}
                        radiusStyle={layoutStyle}
                    />
                ),
                { position: 'top-right', duration: 2000 },
            );
            router.refresh();
        } catch (error: any) {
            setAddingItem(false);
            toast.error(error.message, { position: 'bottom-center' });
        }
    };

    return (
        <div
            id={`product-card-button-beside-price-style-${product.id}`}
            className="flex flex-col gap-6 justify-start items-start w-full h-full"
        >
            <div
                id="product-card-button-beside-price-image-container"
                className="relative w-full flex justify-center items-center aspect-square min-h-240 sm:min-h-480 rounded-3xl border border-[#DDD]"
                style={{ maxWidth: '100%' }}
            >
                <a
                    id={`product-card-button-beside-price-link`}
                    href={href}
                    className="w-full h-full "
                >
                    <SmartImage
                        id={`product-item-img`}
                        src={imageUrl}
                        alt={imageAlt || 'Product image'}
                        width={480}
                        height={480}
                        className="h-full w-full object-contain bg-white rounded-3xl"
                        loading={priorityImage ? 'eager' : 'lazy'}
                        preferResize={true}
                    />
                </a>
                {/* <div className="p-1 bg-white rounded-full w-7 h-7 absolute top-2 right-2"> */}
                {/* <span className="material-symbols-outlined text-lg w-full text-center">favorite</span> */}
                {favoriteEnable && (
                    <CollectionListItemClient
                        productHandle={product.handle}
                        refresh={refreshfav}
                    />
                )}
                <div
                    id="product-card-button-beside-price-tag-container"
                    className={`absolute top-0 left-0 flex items-center justify-start p-3 ${
                        favoriteEnable ? 'w-[calc(100%-44px)]' : 'w-full'
                    }`}
                >
                    <ProductTagRenderer tags={product.tags} singleRow={true} />
                </div>
                {/* </div> */}
            </div>
            <div
                id="product-card-button-beside-price-info"
                className="flex flex-col gap-5 justify-between w-full h-full"
            >
                <div id={`product-item-title-container`}>
                    <a
                        id={`product-item-link`}
                        href={href}
                        className="font-semibold text-lg leading-8 hover:opacity-50 dynamic-color"
                        style={{
                            color: colorStyle?.text || '#3d3d3d',
                        }}
                    >
                        <span
                            id={`product-item-title`}
                            className="line-clamp-3"
                        >
                            {title}
                        </span>
                    </a>
                </div>
                <div
                    id="product-card-button-beside-price-info-bottom"
                    className="flex flex-col gap-5 justify-end w-full h-full"
                >
                    {
                        // if price contain key of "STEP_PRICE"
                        nextStep && variant?.show_price ? (
                            <div
                                id={`product-item-next-step`}
                                className="w-full flex flex-col"
                            >
                                <div
                                    id={`product-item-next-step-info`}
                                    className="product-item-next-step-info w-full grid grid-cols-2 sm:items-center items-end gap-2 text-[#0CA182] bg-white p-[10px] rounded-t-lg border border-[#0CA182]"
                                >
                                    <div
                                        id={`product-item-next-step-info-text`}
                                        className="w-full flex flex-col items-start justify-end"
                                    >
                                        <span
                                            id={`product-item-next-step-info-quantity`}
                                            className="text-sm"
                                        >
                                            {t('promotion.step_price_steps', {
                                                step: nextStep.step,
                                            })}
                                        </span>
                                        <span
                                            id={`product-item-next-step-info-price`}
                                            className="w-full flex flex-col gap-2 items-start font-semibold xl:text-3xl lg:text-2xl text-xl leading-8"
                                        >
                                            {nextStep.price[level] !==
                                                undefined && (
                                                <CurrencyPrice
                                                    id={`product-item-next-step-info-price-value`}
                                                    priceString={toPriceString(
                                                        nextStep.price[level]!
                                                            .price,
                                                    )}
                                                    shopCurrency={shopCurrency}
                                                    wrapperClassName="font-semibold"
                                                    priceClassName="xl:text-[30px] lg:text-[24px] text-[20px] leading-8"
                                                    currencyClassName="text-sm"
                                                />
                                            )}
                                            <div className="w-full flex flex-row gap-2 justify-between">
                                                <span
                                                    id={`product-item-next-step-info-per-item`}
                                                    className="text-xs xl:leading-5 leading-6"
                                                >
                                                    {t('promotion.per_item', {
                                                        step: nextStep.step,
                                                    })}
                                                </span>
                                            </div>
                                        </span>
                                    </div>
                                    <div className="w-full flex justify-end items-center">
                                        {orderType !== 'DISABLED' &&
                                            orderType === 'BUY_BUTTON' &&
                                            product.sell_state ==
                                                'READY_FOR_SELL' &&
                                            !isOutOfStock &&
                                            !isAddAllStockInCart &&
                                            variant && (
                                                <button
                                                    id={`product-item-step-price-buy-button`}
                                                    aria-label="Add to cart"
                                                    onClick={handleOnAddCart}
                                                    className="w-fit group border border-[#0CA182] hover:border-[#0CA182] py-2.5 pl-3 pr-3.5 flex items-center justify-center cursor-pointer flex-grow-0 h-auto self-end bg-transparent hover:bg-[#0CA182] transition-colors"
                                                    style={{
                                                        borderRadius:
                                                            borderRadiusShorthand(
                                                                layoutStyle,
                                                            ),
                                                    }}
                                                >
                                                    {isAddingItem ? (
                                                        <Loader2 className="w-[20px] h-[20px] animate-spin" />
                                                    ) : (
                                                        <div className="flex flex-row gap-2 justify-center items-center text-[#0CA182] group-hover:text-white transition-colors">
                                                            <BagCartButton className="w-[16px] h-[18px]" />
                                                            <p className="xl:block hidden relative text-sm font-semibold top-[1px]">
                                                                {t(
                                                                    'add_product',
                                                                )}
                                                            </p>
                                                        </div>
                                                    )}
                                                </button>
                                            )}
                                    </div>
                                </div>
                                <div className="product-item-next-step-info-normal rounded-b-lg w-full bg-[#0CA182] text-white p-[10px]">
                                    <div
                                        id={`product-item-next-step-info-normal`}
                                        className="w-full flex flex-wrap flex-row  gap-px justify-between items-start"
                                    >
                                        <span
                                            id={`product-item-next-step-info-normal-price`}
                                            className="flex flex-col gap-px font-normal text-white"
                                        >
                                            <span style={{ fontSize: '10px' }}>
                                                {t('product.normal')}
                                            </span>
                                            <span className="flex flex-row gap-2 items-end">
                                                <span className="line-through xl:text-base lg:text-sm text-xs">
                                                    {formatCurrencyFormat(
                                                        toPriceString(
                                                            stepNormalPrice,
                                                        ),
                                                        shopCurrency,
                                                    )}
                                                </span>
                                                <span
                                                    id={`product-item-next-step-info-per-item`}
                                                    style={{ fontSize: '10px' }}
                                                >
                                                    {t('promotion.per_item', {
                                                        step: nextStep.step,
                                                    })}
                                                </span>
                                            </span>
                                        </span>
                                        <span
                                            id={`product-item-next-step-info-normal-price-percentage`}
                                            className="text-xs font-semibold  self-end"
                                        >
                                            {t('promotion.saved_percentage', {
                                                percentage: Math.floor(
                                                    nextStep.price[level]
                                                        ?.percentage || 0,
                                                ),
                                            })}
                                        </span>
                                    </div>
                                </div>
                            </div>
                        ) : null
                    }
                    {variant?.show_price && !nextStep && (
                        <div>
                            <div
                                id={`product-item-price`}
                                className="flex flex-col w-full"
                            >
                                {/* BP Price */}
                                {level === 'BP' && (
                                    <div
                                        id={`product-item-bp-price`}
                                        className="flex lg:flex-row flex-col lg:justify-between justify-start lg:items-end items-start w-full lg:gap-0.5 gap-3"
                                    >
                                        <div
                                            id={`product-item-bp-price-info`}
                                            className="flex flex-col flex-wrap justify-end"
                                        >
                                            {isTrialHaveSpecialPrice &&
                                                trialNormalPrice && (
                                                    <span
                                                        id={`product-item-price-normal`}
                                                        className="line-through font-normal text-sm"
                                                        style={{
                                                            color:
                                                                colorStyle?.text ||
                                                                '#3d3d3d',
                                                            opacity: 0.5,
                                                        }}
                                                    >
                                                        {formatCurrencyFormat(
                                                            toPriceString(
                                                                trialNormalPrice,
                                                            ),
                                                            shopCurrency,
                                                        )}
                                                    </span>
                                                )}
                                            {trialPrice !== null &&
                                                trialPrice !== undefined && (
                                                    <span
                                                        id={`product-item-price-special`}
                                                        className="font-semibold text-xl leading-8"
                                                        style={{
                                                            color:
                                                                colorStyle?.text ||
                                                                '#3d3d3d',
                                                        }}
                                                    >
                                                        {formatCurrencyFormat(
                                                            toPriceString(
                                                                trialPrice,
                                                            ),
                                                            shopCurrency,
                                                        )}
                                                    </span>
                                                )}
                                        </div>
                                        {orderType !== 'DISABLED' &&
                                            orderType === 'BUY_BUTTON' &&
                                            product.sell_state ==
                                                'READY_FOR_SELL' &&
                                            !isOutOfStock &&
                                            !isAddAllStockInCart &&
                                            variant && (
                                                <button
                                                    id={`product-item-bp-price-info-button`}
                                                    aria-label={
                                                        product.isSubscriptionProduct
                                                            ? 'View product detail'
                                                            : 'Add to cart'
                                                    }
                                                    onClick={
                                                        product.isSubscriptionProduct
                                                            ? () =>
                                                                  router.push(
                                                                      href,
                                                                  )
                                                            : handleOnAddCart
                                                    }
                                                    className="lg:w-fit w-full group border border-[#0CA182] hover:border-[#0CA182] py-2.5 pl-3 pr-3.5 flex items-center justify-center cursor-pointer flex-grow-0 h-auto self-end bg-transparent hover:bg-[#0CA182] transition-colors"
                                                    style={{
                                                        borderRadius:
                                                            borderRadiusShorthand(
                                                                layoutStyle,
                                                            ),
                                                    }}
                                                >
                                                    {!product.isSubscriptionProduct &&
                                                    isAddingItem ? (
                                                        <Loader2 className="w-[20px] h-[20px] animate-spin" />
                                                    ) : (
                                                        <div className="flex flex-row gap-2 justify-center items-center text-[#0CA182] group-hover:text-white transition-colors">
                                                            <BagCartButton className="w-[16px] h-[18px]" />
                                                            <p className="relative text-sm font-semibold top-[1px]">
                                                                {product.isSubscriptionProduct
                                                                    ? text[
                                                                          'view_product_detail'
                                                                      ]
                                                                    : t(
                                                                          'add_product',
                                                                      )}
                                                            </p>
                                                        </div>
                                                    )}
                                                </button>
                                            )}
                                    </div>
                                )}
                                {/* Normal Price */}
                                {level === 'NORMAL' ||
                                    (level === 'GUEST' && (
                                        <div
                                            id={`product-item-normal-price`}
                                            className="flex lg:flex-row flex-col lg:justify-between justify-start lg:items-end items-start w-full lg:gap-0.5 gap-3"
                                        >
                                            <div
                                                id={`product-item-normal-price-info`}
                                                className="flex flex-col flex-wrap justify-end"
                                            >
                                                {isFullHaveSpecialPrice &&
                                                    normalPrice && (
                                                        <span
                                                            id={`product-item-price-normal`}
                                                            className="line-through font-normal text-sm"
                                                            style={{
                                                                color:
                                                                    colorStyle?.text ||
                                                                    '#3d3d3d',
                                                                opacity: 0.5,
                                                            }}
                                                        >
                                                            {formatCurrencyFormat(
                                                                toPriceString(
                                                                    normalPrice,
                                                                ),
                                                                shopCurrency,
                                                            )}
                                                        </span>
                                                    )}
                                                {price !== null &&
                                                    price !== undefined && (
                                                        <span
                                                            id={`product-item-price-special`}
                                                            className="font-semibold text-xl leading-8"
                                                            style={{
                                                                color:
                                                                    colorStyle?.text ||
                                                                    '#3d3d3d',
                                                            }}
                                                        >
                                                            {formatCurrencyFormat(
                                                                toPriceString(
                                                                    price,
                                                                ),
                                                                shopCurrency,
                                                            )}
                                                        </span>
                                                    )}
                                            </div>
                                            {orderType !== 'DISABLED' &&
                                                orderType === 'BUY_BUTTON' &&
                                                product.sell_state ==
                                                    'READY_FOR_SELL' &&
                                                !isOutOfStock &&
                                                !isAddAllStockInCart &&
                                                variant && (
                                                    <button
                                                        id={`product-item-bp-price-info-button`}
                                                        aria-label={
                                                            product.isSubscriptionProduct
                                                                ? 'View product detail'
                                                                : 'Add to cart'
                                                        }
                                                        onClick={
                                                            product.isSubscriptionProduct
                                                                ? () =>
                                                                      router.push(
                                                                          href,
                                                                      )
                                                                : handleOnAddCart
                                                        }
                                                        className="lg:w-fit w-full group border border-[#0CA182] hover:border-[#0CA182] py-2.5 pl-3 pr-3.5 flex items-center justify-center cursor-pointer flex-grow-0 h-auto self-end bg-transparent hover:bg-[#0CA182] transition-colors"
                                                        style={{
                                                            borderRadius:
                                                                borderRadiusShorthand(
                                                                    layoutStyle,
                                                                ),
                                                        }}
                                                    >
                                                        {!product.isSubscriptionProduct &&
                                                        isAddingItem ? (
                                                            <Loader2 className="w-[20px] h-[20px] animate-spin" />
                                                        ) : (
                                                            <div className="flex flex-row gap-2 justify-center items-center text-[#0CA182] group-hover:text-white transition-colors">
                                                                <BagCartButton className="w-[16px] h-[18px]" />
                                                                <p className="relative text-sm font-semibold top-[1px]">
                                                                    {product.isSubscriptionProduct
                                                                        ? text[
                                                                              'view_product_detail'
                                                                          ]
                                                                        : t(
                                                                              'add_product',
                                                                          )}
                                                                </p>
                                                            </div>
                                                        )}
                                                    </button>
                                                )}
                                        </div>
                                    ))}
                            </div>
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
};

// Comparator: re-render only if key props or core product pricing/tag info change.
// (Conservative: if unsure, return false to allow update.)
const areEqual = (
    prev: CollectionListItemProps,
    next: CollectionListItemProps,
) => {
    if (prev === next) return true;
    if (prev.href !== next.href) return false;
    if (prev.level !== next.level) return false;
    if (prev.variantId !== next.variantId) return false;
    if (prev.priorityImage !== next.priorityImage) return false;
    if (prev.shopCurrency !== next.shopCurrency) return false;
    if (prev.refreshfav !== next.refreshfav) return false;
    const pp = prev.product;
    const np = next.product;
    if (pp === np) return true; // same reference
    if (!pp || !np) return false;
    if (pp.id !== np.id) return false;
    // Compare basic displayed fields
    if (pp.attributes?.displayName !== np.attributes?.displayName) return false;
    // Compare variant counts & first variant core pricing (most cards show first variant)
    const pVar0 = pp.productVariant?.[0];
    const nVar0 = np.productVariant?.[0];
    if (!!pVar0 !== !!nVar0) return false;
    if (pVar0 && nVar0) {
        if (pVar0.id !== nVar0.id) return false;
        if (pVar0.priceFull !== nVar0.priceFull) return false;
        if (pVar0.comparePriceFull !== nVar0.comparePriceFull) return false;
        if (pVar0.priceTrial !== nVar0.priceTrial) return false;
        if (pVar0.comparePriceTrial !== nVar0.comparePriceTrial) return false;
    }
    // Compare tag ids (order-insensitive) length + ids set
    const pTags = pp.tags || [];
    const nTags = np.tags || [];
    if (pTags.length !== nTags.length) return false;
    for (let i = 0; i < pTags.length; i++) {
        if (pTags[i].id !== nTags[i].id) return false; // assumes stable ordering
    }
    return true; // no significant changes detected
};

const Loading = () => {
    return (
        <div className="flex flex-col gap-6 justify-start items-start w-full h-full animate-pulse">
            <div
                className="relative w-full flex justify-center items-center aspect-square min-h-240 sm:min-h-480"
                style={{ maxWidth: '100%' }}
            >
                <div className="h-full w-full bg-gray-200 rounded-3xl" />
            </div>
            <div className="flex flex-col gap-5 justify-between w-full h-full">
                <div className="h-12 w-full bg-gray-200 rounded" />
                <div className="flex gap-2 items-center">
                    <div className="h-6 w-6 bg-gray-200 rounded-full" />
                    <div className="h-6 w-6 bg-gray-200 rounded-full" />
                    <div className="h-6 w-6 bg-gray-200 rounded-full" />
                </div>
                <div className="h-6 w-1/4 bg-gray-200 rounded" />
                <div className="h-6 w-1/3 bg-gray-200 rounded" />
            </div>
        </div>
    );
};

const ProductCardButtonBesidePrice = memo(
    ProductCardButtonBesidePriceInner,
    areEqual,
);

export default dynamic(() => Promise.resolve(ProductCardButtonBesidePrice), {
    ssr: false,
    loading: Loading,
});
